import { shallowCloneObject } from "../../../base/common/helpers.js";
import { ILogService } from "../../log/common/log.js";
import { GetAccessorOptions, IPropertyAccessorService, IPropertyValueAccessor, IPropertyValueAccessorWithCloneAbility, IPropertyValueAccessorWithReadAbility, IPropertyValueAccessorWithWriteAbility, isPropertyValueAccessorWithCloneAbility, isPropertyValueAccessorWithMetadataReadAbility, isPropertyValueAccessorWithMetadataWriteAbility, isPropertyValueAccessorWithReadAbility, isPropertyValueAccessorWithWriteAbility, Metadata, PropertyAccessContext } from "./accessor.js";
import { IProperty, ValueMetadata } from "./properties.js";

export class PropertyAccessorService implements IPropertyAccessorService {

    private static _instance: PropertyAccessorService;
    static get Main() {
        if (!this._instance) {
            throw new Error("PropertyAccessorService not initialized yet");
        }
        return this._instance;
    }

    private readonly accessors: IPropertyValueAccessor[] = [];

    constructor(
        @ILogService private readonly logger: ILogService,
    ) {
        if (!PropertyAccessorService._instance) PropertyAccessorService._instance = this;

        this.registerAccessor(new DefaultPropertyAccessor(logger));
    }


    registerAccessor(accessor: IPropertyValueAccessor): void {
        if (this.accessors.includes(accessor)) {
            return;
        }
        this.accessors.push(accessor);
    }

    protected *withAccessor<T extends IPropertyValueAccessor>(predicate: (accessor: IPropertyValueAccessor) => accessor is T): IterableIterator<T> {
        for (let i = this.accessors.length - 1; i >= 0; i--) {
            const accessor = this.accessors[i];
            if (predicate(accessor)) {
                yield accessor;
            }
        }
    }

    protected withReadAccessor(property: IProperty): IPropertyValueAccessorWithReadAbility {
        const meta = {
            accept: property.metadata.accept,
            static: property.metadata.static,
        }
        for (const accessor of this.withAccessor(isPropertyValueAccessorWithReadAbility)) {
            if (accessor.canRead(property.owner, property.key, meta)) {
                return accessor;
            }
        }
        throw new Error("No read accessor found for property");
    }
    protected withWriteAccessor(property: IProperty, newValue: unknown): IPropertyValueAccessorWithWriteAbility {
        const meta = {
            accept: property.metadata.accept,
            static: property.metadata.static,
        }
        for (const accessor of this.withAccessor(isPropertyValueAccessorWithWriteAbility)) {
            const canWrite = accessor.canWrite(property.owner, property.key, newValue, meta);
            if (canWrite) {
                this.logger.debug(`Accessor ${accessor.constructor.name} can write property ${property.key} with value: ${typeof newValue}`);
                return accessor;
            }
        }
        throw new Error("No write accessor found for property");
    }
    protected withCloneAccessor(property: IProperty): IPropertyValueAccessorWithCloneAbility {

        const value = (property.owner as any)[property.key];
        const meta = {
            accept: property.metadata.accept,
            static: property.metadata.static,
        }
        for (const accessor of this.withAccessor(isPropertyValueAccessorWithCloneAbility)) {
            if (accessor.canClone(property.owner, property.key, value, meta)) {
                this.logger.debug(`Accessor ${accessor.constructor.name} can clone property ${property.key} with value: ${typeof value}`);
                return accessor;
            }
        }
        throw new Error("No clone accessor found for property");
    }


    readValue(property: IProperty): unknown {
        const accessor = this.withReadAccessor(property);
        const { realOwner, realKey, context } = this.resolveKey(property.owner, property.key);
        if (!realOwner) {
            return null;
        }
        return accessor.readValue(realOwner, realKey, context);
    }

    canWrite(property: IProperty, newValue: unknown): boolean {
        const meta = {
            accept: property.metadata.accept,
            static: property.metadata.static,
        }
        for (const accessor of this.withAccessor(isPropertyValueAccessorWithWriteAbility)) {
            if (accessor.canWrite(property.owner, property.key, newValue, meta)) {
                this.logger.debug(`Accessor ${accessor.constructor.name} can write property ${property.key} with value: ${typeof newValue}`);
                return true;
            }
        }
        return false;
    }

    async writeValue(property: IProperty, newValue: unknown, metadata?: ValueMetadata, usageHint?: string): Promise<void> {
        const accessor = this.withWriteAccessor(property, newValue);
        // this.logger.debug(`Writing property ${property.key} with value: ${typeof newValue}`);

        const { realOwner, realKey, context } = this.resolveKey(property.owner, property.key);
        if (!realOwner) {
            this.logger.warn(`Failed to write property ${property.key}: cannot resolve owner object.`);
            return;
        }
        // Add usageHint to context if provided
        if (usageHint) {
            context.usageHint = usageHint;
        }
        return accessor.writeValue(realOwner, realKey, newValue, context).then(res => {
            // we write the metadata AFTER setting the value since the accessor might have converted it
            if (metadata) this.writeMetadata(property.value, metadata);
        })
    }

    cloneValue(property: IProperty): unknown {
        const accessor = this.withCloneAccessor(property);
        const currentValue = this.readValue(property);
        return accessor.cloneValue(property.owner, property.key, currentValue);
    }

    async writeMetadata(object: unknown, metadata: ValueMetadata): Promise<void> {
        for (const accessor of this.withAccessor(isPropertyValueAccessorWithMetadataWriteAbility)) {
            if (accessor.writeMetadata && accessor.canWriteMetadata(object as object, metadata)) {
                await accessor.writeMetadata(object as object, metadata);
            }
        }
    }

    async readMetadata(object: unknown): Promise<ValueMetadata | undefined> {
        for (const accessor of this.withAccessor(isPropertyValueAccessorWithMetadataReadAbility)) {
            if (accessor.readMetadata && accessor.canReadMetadata(object as object)) {
                return accessor.readMetadata(object as object);
            }
        }
        return undefined;
    }

    private resolveKey(object: object, key: string): { realOwner: object | null, realKey: string, context: PropertyAccessContext } {
        const path = key.split(".");
        const context: PropertyAccessContext = {
            originalObject: object,
            originalKey: key,
        };
        let realOwner = object;
        if (!realOwner) {
            context.failedTraverse = true;
        }
        if (realOwner && path.length > 1) {
            for (let i = 0; i < path.length - 1; i++) {
                if (!realOwner) {
                    context.failedTraverse = true;
                    break;
                }
                const key = path[i];
                realOwner = (realOwner as any)[key];
            }
        }
        let realKey = path[path.length - 1];
        return { realOwner, realKey, context };
    }
}



class DefaultPropertyAccessor implements IPropertyValueAccessor {

    constructor(private readonly logger: ILogService) {

    }

    canRead(object: object, key: string, meta: Metadata): boolean {
        return true;
    }
    readValue(object: object, key: string): any {
        return (object as any)[key];
    }

    canWrite(object: object, key: string, newValue: unknown, meta: Metadata): boolean {
        return true;
    }
    async writeValue(object: object, key: string, newValue: any) {
        try {
            (object as any)[key] = newValue;
        }
        catch (err) {
            this.logger.error(`Failed to write property ${key}: `, err);
        }
    }

    canClone(object: object, key: string, value: unknown, meta: Metadata): boolean {
        return true;
    }
    cloneValue(object: object, key: string, value: any): any {
        // For primitive types, just return the value
        if (value === null || value === undefined || typeof value !== "object") {
            return value;
        }
        // For arrays, create a shallow copy
        if (Array.isArray(value)) {
            return [...value];
        }
        // For objects, create a shallow copy
        return shallowCloneObject(value);
    }
}
